import { useNavigate } from "react-router-dom";
import { MenuType } from "../../../types/enum";
import { getUrlPathFromComponentPath } from "./routeMapping";
import { Module, SubMenu } from "../../../models/menu/menu.model";
import { MenuRoutes } from "./routes";
import RouteConstant from "../../../constants/route.constants";

/**
 * Menu Navigation Utility
 * 
 * Provides type-safe navigation based on MenuType enum values using cached menu data.
 * 
 * @example
 * ```typescript
 * import { usePageNavigation } from "../user-menu/menu/navigation";
 * import { MenuType } from "../../types/enum";
 * 
 * const navigateToPage = usePageNavigation();
 * 
 * // Navigate using MenuType
 * navigateToPage(MenuType.ViewAllComplaints);
 * 
 * // Navigate with state
 * navigateToPage(MenuType.ViewAllComplaints, { 
 *   state: { complaintType: 'service' } 
 * });
 * ```
 */

/**
 * Navigation options for page navigation
 */
interface NavigationOptions {
    replace?: boolean;
    state?: any;
}

/**
 * Menu item result from search
 */
interface MenuItemResult {
    route: string | null;
    displayName: string;
}

/**
 * Finds menu item by menuId across all modules
 * @param modules - Array of menu modules to search
 * @param menuId - MenuType enum value to find
 * @returns Menu item with route and display name, or null if not found
 */
function findMenuItemById(modules: Module[], menuId: number): MenuItemResult | null {
    for (const module of modules) {
        for (const menu of module.menus) {
            // Check if the menu itself matches
            if (menu.menuId === menuId) {
                return { route: menu.route, displayName: menu.displayName };
            }

            // Check submenus if they exist
            if (menu.subMenu?.length) {
                const subMenuItem = menu.subMenu.find((sm: SubMenu) => sm.menuId === menuId);
                if (subMenuItem) {
                    return { route: subMenuItem.route, displayName: subMenuItem.displayName };
                }
            }
        }
    }
    return null;
}

/**
 * Custom hook for type-safe menu navigation
 * 
 * @returns Navigation function that accepts MenuType and optional navigation options
 * 
 * @example
 * ```typescript
 * const navigateToPage = usePageNavigation();
 * 
 * // Simple navigation
 * navigateToPage(MenuType.ViewAllComplaints);
 * 
 * // With state parameter
 * navigateToPage(MenuType.ViewAllComplaints, { 
 *   state: { complaintType: 'service' } 
 * });
 * 
 * // Replace current history entry
 * navigateToPage(MenuType.Dashboard, { replace: true });
 * ```
 */
export function usePageNavigation() {
    const navigate = useNavigate();
    const { menuData } = MenuRoutes();

    return (menuType: MenuType, options?: NavigationOptions): boolean => {
        try {
            // Validate menu data availability
            if (!menuData?.length) {
                console.error("[Navigation] No menu data available");
                return false;
            }

            // Find the menu item
            const menuItem = findMenuItemById(menuData, menuType);
            if (!menuItem?.route) {
                // Fallback for Reward Details which might be suppressed by backend when points are 0
                if (menuType === MenuType.RewardDeatils) {
                    navigate(RouteConstant.myRewards, {
                        replace: options?.replace,
                        state: options?.state
                    });
                    return true;
                }

                console.error(
                    `[Navigation] ${!menuItem ? 'Menu item not found' : 'No route defined'} for MenuType: ${menuType}`
                );
                return false;
            }

            // Generate URL and navigate
            const urlPath = getUrlPathFromComponentPath(menuItem.route, menuItem.displayName);
            navigate(urlPath, {
                replace: options?.replace,
                state: options?.state
            });

            return true;
        } catch (error) {
            console.error("[Navigation] Navigation error:", error);
            return false;
        }
    };
}

/**
 * Custom hook to get menu URL path
 * 
 * @returns Function that returns URL path for a given MenuType, or null if not found
 * 
 * @example
 * ```typescript
 * const getMenuUrl = useMenuUrl();
 * const url = getMenuUrl(MenuType.ViewAllComplaints);
 * if (url) {
 *   console.log(url); // "/farm2me/view-all-complaints"
 * }
 * ```
 */
export function useMenuUrl() {
    const { menuData } = MenuRoutes();

    return (menuType: MenuType): string | null => {
        try {
            if (!menuData?.length) return null;

            const menuItem = findMenuItemById(menuData, menuType);
            if (!menuItem?.route) return null;

            return getUrlPathFromComponentPath(menuItem.route, menuItem.displayName);
        } catch (error) {
            console.error("[Navigation] Error getting menu URL:", error);
            return null;
        }
    };
}

/**
 * Custom hook to check menu accessibility
 * 
 * @returns Function that checks if a MenuType is accessible in current menu data
 * 
 * @example
 * ```typescript
 * const isMenuAccessible = useMenuAccessibility();
 * if (isMenuAccessible(MenuType.AdminPanel)) {
 *   // Show admin panel option
 * }
 * ```
 */
export function useMenuAccessibility() {
    const { menuData } = MenuRoutes();

    return (menuType: MenuType): boolean => {
        try {
            if (!menuData?.length) return false;

            const menuItem = findMenuItemById(menuData, menuType);
            return Boolean(menuItem?.route);
        } catch (error) {
            console.error("[Navigation] Error checking accessibility:", error);
            return false;
        }
    };
}
